/**
 * React Query hooks — every API call goes through one of these. Components
 * never call fetch() directly.
 *
 * Conventions:
 *   - useX = read (useQuery)
 *   - useXMutation = write (useMutation), invalidates relevant keys on success
 *   - keys are stable arrays under `keys.*`
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient, UseMutationResult } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  Credential,
  Folder,
  McpToken,
  Note,
  NoteColor,
  SearchResult,
  Share,
  User,
  UserConfig,
} from "./types";

export const keys = {
  me: ["me"] as const,
  notes: (filter?: NotesFilter) => ["notes", filter ?? {}] as const,
  note: (id: string) => ["notes", id] as const,
  folders: ["folders"] as const,
  credentials: ["credentials"] as const,
  config: ["config"] as const,
  vapidKey: ["vapid-key"] as const,
  mcpTokens: ["mcp-tokens"] as const,
  mcpGrants: ["mcp-grants"] as const,
  search: (q: string, mode: string) => ["search", q, mode] as const,
  shares: (noteId: string) => ["shares", noteId] as const,
  publicShare: (token: string) => ["public-share", token] as const,
};

export type NoteSort = "updated" | "created" | "folder";

export interface NotesFilter {
  folder?: string | "none" | null;
  archived?: boolean;
  trashed?: boolean;
  has_reminder?: boolean;
  /** Sort key (pinned always lead regardless). Server defaults to "updated". */
  sort?: NoteSort;
}

function notesPath(filter: NotesFilter): string {
  const params = new URLSearchParams();
  if (filter.folder !== undefined && filter.folder !== null) {
    params.set("folder", filter.folder);
  }
  if (filter.archived !== undefined) params.set("archived", String(filter.archived));
  if (filter.trashed !== undefined) params.set("trashed", String(filter.trashed));
  if (filter.has_reminder !== undefined) params.set("has_reminder", String(filter.has_reminder));
  if (filter.sort) params.set("sort", filter.sort);
  const s = params.toString();
  return s ? `/notes?${s}` : "/notes";
}

// ---------------------------------------------------------------------------
// Me + auth
// ---------------------------------------------------------------------------

export function useMe(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<{ user: User }>("/me").then((r) => r.user),
    retry: false,
    enabled: opts.enabled,
  });
}

export function useCredentials() {
  return useQuery({
    queryKey: keys.credentials,
    queryFn: () =>
      api.get<{ credentials: Credential[] }>("/me/credentials").then((r) => r.credentials),
  });
}

export function useRenameCredential(): UseMutationResult<
  void,
  Error,
  { id: string; name: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }) =>
      api.patch(`/me/credentials/${id}`, { name }).then(() => undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.credentials }),
  });
}

export function useDeleteCredential(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/me/credentials/${id}`).then(() => undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.credentials }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSuccess: () => {
      qc.clear();
    },
  });
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

export function useNotes(filter: NotesFilter = {}) {
  return useQuery({
    queryKey: keys.notes(filter),
    queryFn: () => api.get<{ notes: Note[] }>(notesPath(filter)).then((r) => r.notes),
  });
}

export function useNote(id: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.note(id),
    queryFn: () => api.get<{ note: Note }>(`/notes/${id}`).then((r) => r.note),
    enabled: opts.enabled ?? !!id,
  });
}

/** Every note-mutating action also dirties the sidebar's per-folder note
 *  count and any cached search result set (since the trashed/restored note
 *  may still be in there). Centralizing avoids "forgot to invalidate X"
 *  bugs at each call site. */
function invalidateNotesAndFolders(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ["notes"] });
  qc.invalidateQueries({ queryKey: keys.folders });
  qc.invalidateQueries({ queryKey: ["search"] });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Note>) =>
      api.post<{ note: Note }>("/notes", input).then((r) => r.note),
    onSuccess: () => invalidateNotesAndFolders(qc),
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Partial<Note>) =>
      api.patch<{ note: Note }>(`/notes/${id}`, input).then((r) => r.note),
    onMutate: async ({ id, ...input }) => {
      await qc.cancelQueries({ queryKey: ["notes"] });
      const previous = qc.getQueryData<Note>(keys.note(id));
      if (previous) {
        qc.setQueryData<Note>(keys.note(id), { ...previous, ...input } as Note);
      }
      return { previous };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.note(vars.id), ctx.previous);
    },
    onSettled: () => invalidateNotesAndFolders(qc),
  });
}

export function useTrashNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/notes/${id}`),
    onSuccess: () => invalidateNotesAndFolders(qc),
  });
}

export function useRestoreNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/notes/${id}/restore`),
    onSuccess: () => invalidateNotesAndFolders(qc),
  });
}

export function useDeleteForever() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/notes/${id}/forever`),
    onSuccess: () => invalidateNotesAndFolders(qc),
  });
}

export function useEmptyTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/notes/trash/empty"),
    onSuccess: () => invalidateNotesAndFolders(qc),
  });
}

export function useBulkAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      ids: string[];
      action: string;
      color?: NoteColor;
      folder_id?: string | null;
    }) => api.post("/notes/bulk", body),
    onSuccess: () => invalidateNotesAndFolders(qc),
  });
}

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

export function useFolders() {
  return useQuery({
    queryKey: keys.folders,
    queryFn: () => api.get<{ folders: Folder[] }>("/folders").then((r) => r.folders),
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; color?: string }) =>
      api.post<{ folder: Folder }>("/folders", input).then((r) => r.folder),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.folders }),
  });
}

export function useUpdateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; color?: string | null }) =>
      api.patch<{ folder: Folder }>(`/folders/${id}`, input).then((r) => r.folder),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.folders }),
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/folders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.folders });
      qc.invalidateQueries({ queryKey: ["notes"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

export function useSearch(q: string, mode: "text" | "semantic" | "hybrid") {
  return useQuery({
    queryKey: keys.search(q, mode),
    queryFn: () =>
      api
        .get<{
          results: SearchResult[];
          mode: string;
        }>(`/search?q=${encodeURIComponent(q)}&mode=${mode}`)
        .then((r) => r.results),
    enabled: q.trim().length > 0,
    staleTime: 5_000,
  });
}

// ---------------------------------------------------------------------------
// Shares
// ---------------------------------------------------------------------------

export function useShares(noteId: string) {
  return useQuery({
    queryKey: keys.shares(noteId),
    queryFn: () => api.get<{ shares: Share[] }>(`/shares/note/${noteId}`).then((r) => r.shares),
    enabled: !!noteId,
  });
}

export function useCreateShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      noteId: string;
      permission: "read" | "write";
      expires_at?: string | null;
    }) =>
      api
        .post<{ share: Share }>(`/shares/note/${input.noteId}`, {
          permission: input.permission,
          expires_at: input.expires_at,
        })
        .then((r) => r.share),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: keys.shares(vars.noteId) }),
  });
}

export function useRevokeShare(noteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => api.delete(`/shares/${shareId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.shares(noteId) }),
  });
}

// ---------------------------------------------------------------------------
// Config + AI
// ---------------------------------------------------------------------------

export function useConfig() {
  return useQuery({
    queryKey: keys.config,
    queryFn: () => api.get<{ config: UserConfig }>("/config").then((r) => r.config),
  });
}

export function useUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<UserConfig>) =>
      api.patch<{ config: UserConfig }>("/config", input).then((r) => r.config),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.config }),
  });
}

export function useSetNtfyToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api.put<{ config: UserConfig }>("/config/ntfy-token", { token }).then((r) => r.config),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.config }),
  });
}

export function useDeleteNtfyToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete("/config/ntfy-token"),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.config }),
  });
}

export function useSetOpenAIKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      api.put<{ config: UserConfig }>("/config/openai-key", { key }).then((r) => r.config),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.config }),
  });
}

export function useDeleteOpenAIKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete("/config/openai-key"),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.config }),
  });
}

export function useTestOpenAIKey() {
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean; reply: string }>("/config/openai-test"),
  });
}

// ---------------------------------------------------------------------------
// MCP access tokens
// ---------------------------------------------------------------------------

export function useMcpTokens() {
  return useQuery({
    queryKey: keys.mcpTokens,
    queryFn: () => api.get<{ tokens: McpToken[] }>("/mcp-tokens").then((r) => r.tokens),
  });
}

export function useCreateMcpToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<{ token: string; row: McpToken }>("/mcp-tokens", { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.mcpTokens }),
  });
}

export function useRevokeMcpToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/mcp-tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.mcpTokens }),
  });
}

/** An app connected over the OAuth (claude.ai) connector. */
export interface McpGrant {
  client_id: string;
  client_name: string;
  created_at: number;
  last_used_at: number | null;
}

export function useMcpGrants() {
  return useQuery({
    queryKey: keys.mcpGrants,
    queryFn: () => api.get<{ grants: McpGrant[] }>("/mcp-tokens/grants").then((r) => r.grants),
  });
}

export function useRevokeMcpGrant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => api.delete(`/mcp-tokens/grants/${clientId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.mcpGrants }),
  });
}

// ---------------------------------------------------------------------------
// Notifications (Web Push + ntfy)
// ---------------------------------------------------------------------------

/** The app's VAPID public key, or null if Web Push isn't configured server-side. */
export function useVapidPublicKey() {
  return useQuery({
    queryKey: keys.vapidKey,
    queryFn: () =>
      api.get<{ key: string | null }>("/notifications/vapid-public-key").then((r) => r.key),
    staleTime: Infinity,
  });
}

/** Fire a test notification to the user's configured channels right now. */
export function useTestNotification() {
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; ntfy: boolean; push: number; vapid_configured: boolean }>(
        "/notifications/test"
      ),
  });
}

export function useAISimplify() {
  return useMutation({
    mutationFn: (text: string) =>
      api.post<{ text: string }>("/ai/simplify", { text }).then((r) => r.text),
  });
}

export function useAIFix() {
  return useMutation({
    mutationFn: (text: string) =>
      api.post<{ text: string }>("/ai/fix", { text }).then((r) => r.text),
  });
}
